/**
 * Main dashboard page showing summary cards, severity chart, recent scans,
 * and top vulnerabilities.
 */

import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/hooks/useAuth';
import SummaryCards from '@/components/dashboard/SummaryCards';
import SeverityChart from '@/components/dashboard/SeverityChart';
import RecentScans from '@/components/dashboard/RecentScans';
import TopVulnerabilities from '@/components/dashboard/TopVulnerabilities';
import LoadingSpinner from '@/components/common/LoadingSpinner';
import { getAssets } from '@/api/assets';
import { getScans } from '@/api/scans';
import { getFindings, getFindingSummary } from '@/api/findings';
import apiClient from '@/api/client';
import {coreApiMode} from '@/config/deployment';

export default function DashboardPage() {
  const { user } = useAuth();
  const {data:counts}=useQuery({queryKey:['dashboard',user?.tenant_id],queryFn:async()=> (await apiClient.get<{assets:number;scans:number;devices?:number;device_limit?:number}>('/v1/dashboard')).data,enabled:coreApiMode});
  const { data: assets, isLoading: assetsLoading } = useQuery({
    queryKey: ['assets'],
    queryFn: () => getAssets(),
  });

  const { data: scans, isLoading: scansLoading } = useQuery({
    queryKey: ['scans'],
    queryFn: () => getScans(),
  });

  const { data: summary, isLoading: summaryLoading } = useQuery({
    queryKey: ['findings', 'summary'],
    queryFn: getFindingSummary,
  });

  const { data: findings } = useQuery({
    queryKey: ['findings', { severity: 'critical,high' }],
    queryFn: async () => {
      const [critical, high] = await Promise.all([
        getFindings({ severity: 'critical' }),
        getFindings({ severity: 'high' }),
      ]);
      return [...critical, ...high];
    },
  });

  const isLoading = assetsLoading || scansLoading || summaryLoading;

  if (isLoading) {
    return <LoadingSpinner label="Loading dashboard..." size="lg" />;
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
        {user?.tenant_name && (
          <p className="mt-0.5 text-sm text-gray-500">{user.tenant_name}</p>
        )}
      </div>

      <SummaryCards
        totalAssets={coreApiMode ? counts?.devices??counts?.assets??0 : assets?.length ?? 0}
        deviceLimit={coreApiMode ? counts?.device_limit : undefined}
        totalScans={coreApiMode ? counts?.scans??0 : scans?.length ?? 0}
        findingSummary={summary ?? null}
      />

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <SeverityChart summary={summary ?? null} />
        <RecentScans scans={scans ?? []} />
      </div>

      <TopVulnerabilities findings={findings ?? []} />
    </div>
  );
}
